import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Close,
  ArrowForward,
  CheckCircle,
  Phone,
  Email,
  Web,
  Code,
  Storage,
  Devices,
  Speed,
  Security,
  Cloud,
  ShoppingCart,
  Business,
  School,
  MedicalServices,
  Wifi,
  AttachMoney,
  Dashboard,
  Analytics,
} from "@mui/icons-material";
import styles from "./WebApp.module.css";

// Services Data
const servicesData = [
  {
    id: 1,
    title: "Custom Web Application Development",
    icon: <Code sx={{ fontSize: 40 }} />,
    description:
      "We develop customized web apps from conception and provide services such as migration from your old application to a new solution and enhancement of the front-end functionality.",
    features: [
      "Custom development",
      "Application migration",
      "Front-end enhancement",
      "Time & budget focused",
    ],
    color: "#8cc61d",
  },
  {
    id: 2,
    title: "Mobile-Friendly Website Development",
    icon: <Devices sx={{ fontSize: 40 }} />,
    description:
      "Our team of usability experts, graphic designers, and veteran front-end professionals will make sure your website looks and works great on any screen size or resolution.",
    features: [
      "Responsive design",
      "UX/UI expertise",
      "Cross-device compatibility",
      "E-commerce solutions",
    ],
    color: "#4a90d9",
  },
  {
    id: 3,
    title: "Database Development",
    icon: <Storage sx={{ fontSize: 40 }} />,
    description:
      "With our considerable experience, we provide database related high quality services and performance. Our certified specialists have profound knowledge of Firebase, SQL and MySQL technologies.",
    features: [
      "Database design",
      "Database development",
      "Performance testing",
      "Data analysis & reporting",
    ],
    color: "#9b59b6",
  },
  {
    id: 4,
    title: "Web Hosting & E-Commerce",
    icon: <ShoppingCart sx={{ fontSize: 40 }} />,
    description:
      "Take advantage of our web design with web hosting offer. Build an app for your business and stay a step above the rest.",
    features: [
      "Web hosting",
      "E-commerce solutions",
      "App development",
      "Business growth",
    ],
    color: "#f39c12",
  },
];

// Database Services
const databaseServices = [
  "Database Design",
  "Database Development",
  "Database Consulting",
  "Database Performance Testing",
  "Data Analysis and Reporting",
];

// Industry Sectors
const sectors = [
  { icon: <MedicalServices />, label: "Healthcare", color: "#e74c3c" },
  { icon: <Wifi />, label: "Telecommunications", color: "#4a90d9" },
  { icon: <AttachMoney />, label: "Financial Services", color: "#2ecc71" },
  { icon: <School />, label: "Education", color: "#8cc61d" },
  { icon: <Business />, label: "Corporate", color: "#9b59b6" },
  { icon: <ShoppingCart />, label: "E-Commerce", color: "#f39c12" },
];

// Tech Stack
const techStack = [
  { name: "React", color: "#61DAFB" },
  { name: "Node.js", color: "#339933" },
  { name: "Firebase", color: "#FFCA28" },
  { name: "MySQL", color: "#4479A1" },
  { name: "MongoDB", color: "#47A248" },
  { name: "Angular", color: "#DD0031" },
  { name: "Vue.js", color: "#4FC08D" },
  { name: "AWS", color: "#FF9900" },
];

// Stats Data
const statsData = [
  { number: "10+", label: "Years Experience", icon: <Code /> },
  { number: "100+", label: "Web Apps Delivered", icon: <Web /> },
  { number: "4.9", label: "Client Rating", icon: <CheckCircle /> },
  { number: "50+", label: "Database Projects", icon: <Storage /> },
];

const WebApp = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const handleOpenDialog = (service) => {
    setSelectedService(service);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedService(null);
  };

  return (
    <Box className={styles.webPage}>
      {/* Hero Section */}
      <Box className={styles.heroSection}>
        <Container maxWidth="lg">
          <Box className={styles.heroContent}>
            <Chip
              label="🌐 Web Application Development"
              className={styles.heroChip}
              size="small"
            />
            <Typography
              variant="h1"
              className={styles.heroTitle}
              sx={{
                fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4rem" },
              }}
            >
              Professional{" "}
              <span className={styles.highlight}>Web Solutions</span>
            </Typography>
            <Typography variant="h6" className={styles.heroSubtitle}>
              Custom web applications, mobile-friendly websites, and database
              solutions
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Intro Section */}
      <Box className={styles.introSection}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 7 }}>
              <Box className={styles.introContent}>
                <Chip
                  label="💡 About Our Services"
                  className={styles.sectionChip}
                  size="small"
                />
                <Typography
                  variant="h2"
                  className={styles.sectionTitle}
                  sx={{
                    fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
                  }}
                >
                  <span className={styles.highlight}>Expert</span> Web
                  Development
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  Our skilled web development team continue to deliver web
                  applications across various organizations in the{" "}
                  <strong>
                    healthcare, telecommunications, financial services and
                    education
                  </strong>{" "}
                  sectors.
                </Typography>
                <Typography variant="body1" className={styles.sectionText}>
                  We are keen to accept your web development projects and
                  excited by the opportunity to resolve any challenges staying
                  within <strong>time and budget limits</strong>.
                </Typography>
                <Box className={styles.offerBox}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    className={styles.offerTitle}
                  >
                    🚀 Special Offer
                  </Typography>
                  <Typography variant="body2" className={styles.offerText}>
                    Take advantage of our{" "}
                    <strong>web design with web hosting offer</strong>. Build an
                    app for your business and stay a step above the rest!
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box className={styles.statsWrapper}>
                <Paper elevation={0} className={styles.statsPaper}>
                  <Grid container spacing={2}>
                    {statsData.map((stat, index) => (
                      <Grid size={{ xs: 6, sm: 6 }} key={index}>
                        <Box className={styles.statCard}>
                          <Box className={styles.statIcon}>{stat.icon}</Box>
                          <Typography
                            variant="h3"
                            className={styles.statNumber}
                          >
                            {stat.number}
                          </Typography>
                          <Typography
                            variant="body2"
                            className={styles.statLabel}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Tech Stack Section */}
      <Box className={styles.techSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="🛠️ Our Tech Stack"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Technologies We <span className={styles.highlight}>Use</span>
            </Typography>
          </Box>

          <Box className={styles.techWrapper}>
            {techStack.map((tech, index) => (
              <Box
                key={index}
                className={styles.techItem}
                sx={{
                  "&:hover": {
                    borderColor: tech.color,
                    boxShadow: `0 4px 20px ${tech.color}30`,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  className={styles.techName}
                  sx={{ color: tech.color }}
                >
                  {tech.name}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Box className={styles.servicesSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="⚡ Our Services"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              What We <span className={styles.highlight}>Offer</span>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {servicesData.map((service) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={service.id}>
                <Card className={styles.serviceCard}>
                  <CardContent>
                    <Box
                      className={styles.serviceIcon}
                      sx={{
                        background: `${service.color}15`,
                        color: service.color,
                      }}
                    >
                      {service.icon}
                    </Box>
                    <Typography variant="h6" className={styles.serviceTitle}>
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={styles.serviceDescription}
                    >
                      {service.description}
                    </Typography>
                    <Box className={styles.serviceFeatures}>
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <Box key={idx} className={styles.serviceFeature}>
                          <CheckCircle
                            className={styles.featureCheck}
                            sx={{ color: service.color }}
                          />
                          <Typography variant="caption">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      className={styles.serviceBtn}
                      onClick={() => handleOpenDialog(service)}
                      endIcon={<ArrowForward />}
                      fullWidth
                      sx={{ background: service.color }}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Database Services Section */}
      <Box className={styles.databaseSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="🗄️ Database Services"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Database <span className={styles.highlight}>Solutions</span>
            </Typography>
            <Typography variant="body1" className={styles.sectionSubtitle}>
              A Database is the core of almost every application. We provide
              high quality database services.
            </Typography>
          </Box>

          <Box className={styles.databaseWrapper}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} className={styles.databaseInfo}>
                  <Typography variant="h6" className={styles.databaseTitle}>
                    Our Database Services Include:
                  </Typography>
                  <Box className={styles.databaseList}>
                    {databaseServices.map((service, index) => (
                      <Box key={index} className={styles.databaseItem}>
                        <CheckCircle className={styles.databaseCheck} />
                        <Typography variant="body2">{service}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} className={styles.databaseTech}>
                  <Typography variant="h6" className={styles.databaseTitle}>
                    Technologies We Use:
                  </Typography>
                  <Box className={styles.databaseTechList}>
                    <Chip
                      label="Firebase"
                      className={styles.dbTechChip}
                      sx={{ background: "#FFCA2820", color: "#FFCA28" }}
                    />
                    <Chip
                      label="SQL"
                      className={styles.dbTechChip}
                      sx={{ background: "#4479A120", color: "#4479A1" }}
                    />
                    <Chip
                      label="MySQL"
                      className={styles.dbTechChip}
                      sx={{ background: "#00758F20", color: "#00758F" }}
                    />
                    <Chip
                      label="MongoDB"
                      className={styles.dbTechChip}
                      sx={{ background: "#47A24820", color: "#47A248" }}
                    />
                    <Chip
                      label="PostgreSQL"
                      className={styles.dbTechChip}
                      sx={{ background: "#33679120", color: "#336791" }}
                    />
                    <Chip
                      label="Redis"
                      className={styles.dbTechChip}
                      sx={{ background: "#DC382D20", color: "#DC382D" }}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Sectors Section */}
      <Box className={styles.sectorsSection}>
        <Container maxWidth="lg">
          <Box className={styles.sectionHeader}>
            <Chip
              label="🏢 Industries We Serve"
              className={styles.sectionChip}
              size="small"
            />
            <Typography
              variant="h2"
              className={styles.sectionTitle}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "2.8rem" },
              }}
            >
              Our <span className={styles.highlight}>Expertise</span>
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {sectors.map((sector, index) => (
              <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                <Box
                  className={styles.sectorCard}
                  sx={{
                    "&:hover": {
                      borderColor: sector.color,
                      boxShadow: `0 8px 32px ${sector.color}20`,
                    },
                  }}
                >
                  <Box
                    className={styles.sectorIcon}
                    sx={{ color: sector.color }}
                  >
                    {sector.icon}
                  </Box>
                  <Typography variant="body2" className={styles.sectorLabel}>
                    {sector.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box className={styles.ctaSection}>
        <Container maxWidth="lg">
          <Box className={styles.ctaContent}>
            <Typography variant="h3" className={styles.ctaTitle}>
              Ready to Build Your{" "}
              <span className={styles.highlight}>Web Application</span>?
            </Typography>
            <Typography variant="body1" className={styles.ctaText}>
              Let's create a powerful web solution that will grow your business
              and engage your customers.
            </Typography>
            <Box className={styles.ctaButtons}>
              <Button
                variant="contained"
                className={styles.ctaPrimaryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
              <Button
                variant="outlined"
                className={styles.ctaSecondaryBtn}
                startIcon={<Email />}
              >
                Email Us
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Service Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        className={styles.dialog}
      >
        {selectedService && (
          <>
            <DialogTitle className={styles.dialogTitle}>
              <Box className={styles.dialogHeader}>
                <Box
                  className={styles.dialogIcon}
                  sx={{
                    background: `${selectedService.color}15`,
                    color: selectedService.color,
                  }}
                >
                  {selectedService.icon}
                </Box>
                <Typography variant="h5" className={styles.dialogTitleText}>
                  {selectedService.title}
                </Typography>
                <IconButton
                  onClick={handleCloseDialog}
                  className={styles.dialogClose}
                >
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <Typography variant="body1" className={styles.dialogDescription}>
                {selectedService.description}
              </Typography>
              <Divider className={styles.dialogDivider} />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                className={styles.dialogFeaturesTitle}
              >
                Key Features:
              </Typography>
              <Box className={styles.dialogFeaturesList}>
                {selectedService.features.map((feature, index) => (
                  <Box key={index} className={styles.dialogFeature}>
                    <CheckCircle
                      className={styles.dialogFeatureCheck}
                      sx={{ color: selectedService.color }}
                    />
                    <Typography variant="body2">{feature}</Typography>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                variant="outlined"
                onClick={handleCloseDialog}
                className={styles.dialogCancelBtn}
              >
                Close
              </Button>
              <Button
                variant="contained"
                className={styles.dialogInquiryBtn}
                startIcon={<Phone />}
              >
                Request a Call Back
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default WebApp;
